import { Address, ChainConfiguration, SmartContractAction } from './utils/types'
import { Token } from './logic/token/token'
import { POLYGON, POLYGON_CONFIG } from './chain/polygon-mainnet'

const seconds = 1000
const minutes = 60 * seconds
export const IMAGES_FOLDER = 'assets/images/'

// 0.01 for 1%
export const riskySlippageThreshold = 0.003
export const defaultSlippage = 0.005
export const maxSwapSlippage = 0.01
// TODO: rename as default, and use only one
export const maxDepositSlippage = 0.003
export const maxWithdrawSlippage = 0.003

export const weiFormat = 'ether'
export const protocolPrecision = 18

export const numericalPlaceholder = '0.0'

export const badPriceThreshold = -0.01 // 1% more expensive than market price

export const maxLookbackPeriodInSec = 3600 * 4 * 1
export const maxLookbackPeriodInBlock = undefined

export const OK_HEARTBEAT = 10 * minutes
export const NOT_OK_HEARTBEAT = 1 * minutes

// TODO : Oracle is probably not needed
export interface TokenDefinition extends Token {
  faucet: Address | undefined
}
// TODO: use rather all-tokens
const matic: Token = {
  symbol: 'MATIC',
  name: 'MATIC',
  address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', // native ?
  decimals: 18,
}
export const wmatic: Token = {
  symbol: 'WMATIC',
  name: 'WMATIC',
  address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270',
  decimals: 18,
}

const dai: Token = {
  symbol: 'DAI',
  name: '(PoS) Dai Stablecoin',
  address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
  decimals: 18,
}

const weth = {
  symbol: 'WETH',
  name: 'Wrapped ETH',
  address: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',
  faucet: '0x92318813Af7fd35F792D8209213c17dd064B4453',
  decimals: 18,
}
const usdc = {
  symbol: 'USDC',
  name: 'USDC',
  address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
  faucet: '0xBe8781a61F7CD217fcF4D4dA819164C0FAF646a9',
  decimals: 6,
}
const wbtc = {
  symbol: 'WBTC',
  name: 'Wrapped BTC',
  address: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6',
  faucet: '0xd8848B9C6Dd843Eceda66056B88a99a97043feA8',
  decimals: 8,
}

/**
 * wmatic is not part of allowed deposit tokens
 */
export const whiteListedDepositToken = [matic, dai, weth, usdc, wbtc]

export interface WhitelistedPool {
  address: Address
  legacies: Address[]
}

export interface ChainMapping {
  factoryContractAddress: Address
  proxy: Address
  poolContractAddress: Address
  legacyPoolContract: Address | undefined
  whitelistPools: WhitelistedPool[]
  whitelist: TokenDefinition[]
  defaultToken: Address
  fromBlock: number
  maxLookbackPeriodInBlock: number
  hasFaucet: boolean
  config: ChainConfiguration
}

//TODO NZ: have a better typing and structure
export const supportedChains: ChainMapping[] = [
  {
    factoryContractAddress: POLYGON.FACTORY,
    // TODO: rename: this is the SWAP pool address, while it's not decided by the backend
    poolContractAddress: POLYGON.POOL,
    proxy: POLYGON.PROXY,
    legacyPoolContract: undefined,
    whitelist: [usdc, weth, wbtc],
    whitelistPools: [
      {
        address: POLYGON.POOL,
        legacies: [],
      },
    ],
    defaultToken: weth.address,
    fromBlock: 10048580,
    maxLookbackPeriodInBlock: 3000,
    hasFaucet: true,
    config: POLYGON_CONFIG,
  },
]

export function getNetworkName(chainId: number): string {
  switch (chainId) {
    case 1:
      return 'ethereum'
    case 56:
      return 'bsc'
    case 137:
      return 'polygon'
    case 42161:
      return 'arbitrum'
    case 43114:
      return 'avalanche'
    case 10:
      return 'optimism'
    case 250:
      return 'fantom'
    case 25:
      return 'chronos'
    case 8217:
      return 'klaytn'
    case 2222:
      return 'kava'
    case 32659:
      return 'fusion'
    case 100:
      return 'gnosis'
    case 42220:
      return 'celo'
    case 1284:
      return 'moonbeam'
    case 1285:
      return 'moonriver'
    case 361:
      return 'theta'
    case 61:
      return 'etc'
  }
  return 'unknown'
}

export function getSupportedChain(): ChainConfiguration {
  return supportedChains[0].config
}

export function getWhitelistPools() {
  return supportedChains[0].whitelistPools.map(element =>
    element.address.toLowerCase()
  )
}
/* Utility function */
export function findTokenDefinition(
  mapping: ChainMapping,
  tokenAddress: string
) {
  return mapping.whitelist.find(t => t.address === tokenAddress)
}

export const defaultChainID = getSupportedChain().appId

export const swaapAppURI = 'https://beta.swaap.finance'
export const swaapDocURI = 'https://docs.swaap.finance'
export const bridgeURI =
  'https://wallet.polygon.technology/polygon/bridge/deposit'
export const rewardsDocsURI = 'https://docs.swaap.finance/docs/faqs/token'

export const allowPoolCreation = false

// use toTwoComplements
export const approveAmount = '10000000000000000000000000000000000000000'
export const absoluteMaxPrice = '10000000000000000000000000000000000000000'

// those number sould be integer representing Wei quantities
export const maxGasEstimates = new Map<SmartContractAction, number>([
  [SmartContractAction.ONE_ASSET_JOIN, 950_000],
  [SmartContractAction.MULTI_ASSET_JOIN, 440_000],
  [SmartContractAction.ONE_ASSET_EXIT, 950_000],
  [SmartContractAction.MULTI_ASSET_EXIT, 190_000],
  [SmartContractAction.SWAP_GIVEN_IN, 700_000],
  [SmartContractAction.SWAP_GIVEN_OUT, 700_000],
])
